// Brief-PDF nach dem Versand in die Akte des Empfängers legen (Personenordner im Objekt, sonst Sammelordner
// 01 Verwaltung/Mieter bzw. /Eigentümer – gleiche Regel wie beim Blanko-Brief, lib/dokumente/briefApi vorschlagOrdner).
import { ladeDateiHoch, ladeOrdnerBaum, verschiebeInPapierkorb, type BaumKnoten } from '../dokumente/aktenschrankApi';
import { POSTAUSGANG_ORDNER, sicherePostausgangOrdner } from '../dokumente/postausgangOrdner';
import { supabase } from '../supabase/client';
import { vorschlagOrdner, type EmpfaengerArt } from '../dokumente/briefApi';
import { briefDateiname } from '../dokumente/blankoBrief';
import { vermerkeAblage } from './epost';

export type AblageErgebnis = { ok: true; pfad: string; dateiId: string; dokumentId: string | null } | { ok: false; grund: string };

const baumCache = new Map<string, Promise<BaumKnoten[]>>();

export async function legeBriefInAkte(a: {
  /** null = kein Post-Brief (z. B. nur E-Mail) → nichts zu vermerken */
  briefId: string | null; pdfBase64: string; betreff: string; objektId: string | null;
  art: EmpfaengerArt | null; empfaengerId: string | null; ordnerName: string | null;
  /** Für den Dateinamen, wenn der Brief im Ordner ohne Objekt landet */
  empfaengerName?: string | null;
}): Promise<AblageErgebnis> {
  // Ohne Objekt: eigener Ordner „Postausgang – Briefe ohne Objekt“ in der geteilten Ablage
  const ohneObjekt = !a.objektId;
  let ordner: { gdriveId: string; pfad: string } | null;
  if (ohneObjekt) ordner = { gdriveId: await sicherePostausgangOrdner(), pfad: POSTAUSGANG_ORDNER };
  else {
    if (!baumCache.has(a.objektId!)) baumCache.set(a.objektId!, ladeOrdnerBaum(a.objektId!));
    ordner = vorschlagOrdner(await baumCache.get(a.objektId!)!, a.art, a.ordnerName);
  }
  if (!ordner) return { ok: false, grund: 'kein passender Ordner in der Akte' };
  const bytes = Uint8Array.from(atob(a.pdfBase64), (c) => c.charCodeAt(0));
  const heute = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
  // Zusatz nach dem Kürzen anhängen – briefDateiname kürzt den Betreff auf 60 Zeichen
  const zusatz = ohneObjekt && a.empfaengerName ? ` (per Post an ${a.empfaengerName.replace(/[\\/:*?"<>|]+/g, ' ').trim().slice(0, 40)})` : ' (per Post)';
  const name = briefDateiname({ datum: heute, betreff: a.betreff }).replace(/\.pdf$/, `${zusatz}.pdf`);
  const { fileId, dokumentId } = await ladeDateiHoch(ordner.gdriveId, new File([bytes], name, { type: 'application/pdf' }), {
    titel: `${a.betreff} (per Post)`,
    mieterId: a.art === 'mieter' && a.empfaengerId ? a.empfaengerId : undefined,
    eigentuemerId: a.art === 'eigentuemer' && a.empfaengerId ? a.empfaengerId : undefined,
  });
  if (a.briefId) await vermerkeAblage(a.briefId, fileId).catch(() => undefined);
  return { ok: true, pfad: ordner.pfad, dateiId: fileId, dokumentId };
}

/** Korrektur: das PDF der abgelehnten Fassung aus der Akte in den Drive-Papierkorb (wiederherstellbar) –
 *  in der Akte bleibt nur der Brief, der wirklich rausgeht. Fehler hier sind unkritisch. */
export async function entferneAlteAblage(ersetztId: string): Promise<void> {
  const { data } = await supabase.from('epost_briefe').select('ablage_datei_id').eq('id', ersetztId).maybeSingle();
  if (data?.ablage_datei_id) await verschiebeInPapierkorb(data.ablage_datei_id).catch(() => undefined);
}
